import { ProductCardImageContainer, ProductCardStyles, ProductCardTextContainer } from '@components/ProductList/styles';
import type { ProductListResult } from '@partful/shared-types';
import { Badge } from '@partful/shared/ui/Badge';
import { Button } from '@partful/shared/ui/Button';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Thumbnail } from 'src/components/Thumbnail';
import { usePermissions } from 'src/hooks/usePermissions';
import { useProductFeatures } from 'src/hooks/useProductFeatures';
import styles from './ProductCard.module.scss';
import { ProductCardActions } from './ProductCardActions';
import { useProductCardStatusBadge } from './useProductCardStatusBadge';

interface ProductCardProps {
  product: ProductListResult;
}

export function ProductCard({ product }: Readonly<ProductCardProps>) {
  const { t } = useTranslation('manageProduct');
  const {
    productId,
    modelStatus,
    thumbnailUrl,
    name: productName,
    enabledFeatures: productEnabledFeatures,
    configurationExternalID,
  } = product;

  const tenantsProductFeatures = useProductFeatures();

  const productFeatures = [...new Set(tenantsProductFeatures.concat(productEnabledFeatures || []))]; // add any product feature not included at tenant level and make it unique to avoid duplication
  const canDeleteProduct = usePermissions(['delete', 'product']);
  const canUpdateProduct = usePermissions(['update', 'product']);
  const canViewPartsCatalogue = usePermissions(['view', 'product.partsCatalogue']);
  const canViewWorkInstructions = usePermissions(['view', 'product.workInstructions']);

  const { status, variant } = useProductCardStatusBadge({ modelStatus });

  const actionButtonsVisible = productFeatures.length > 1;

  // uri encode the product id to be used in the URL as the product name may contain special characters that can break the routing, e.g. slashes
  const urlSafeProductId = encodeURIComponent(productId);

  return (
    <ProductCardStyles data-testid={`product-card-${configurationExternalID}`}>
      <div className={styles.cardToolbar}>
        <Badge variant={variant}>{status}</Badge>
        {(canDeleteProduct || canUpdateProduct) && (
          <Button
            linkComponent={NavLink}
            to={`/manage-product/${urlSafeProductId}?configurationPID=${product.configurationPID}`}
            variant="primary"
          >
            {t('manageProduct')}
          </Button>
        )}
      </div>

      <ProductCardImageContainer>
        {actionButtonsVisible ? (
          <Thumbnail url={thumbnailUrl} alt={productName} />
        ) : (
          <NavLink data-testid="card-navlink" to={`/product-library/${urlSafeProductId}`}>
            <Thumbnail url={thumbnailUrl} alt={productName} />
          </NavLink>
        )}
      </ProductCardImageContainer>

      <ProductCardTextContainer>
        {actionButtonsVisible ? (
          <>
            <span>{productName}</span>
            <ProductCardActions
              data-testid="product-card-actions"
              productFeatures={productFeatures}
              productId={urlSafeProductId}
              canViewPartsCatalogue={canViewPartsCatalogue}
              canViewWorkInstructions={canViewWorkInstructions}
            />
          </>
        ) : (
          <NavLink data-testid="card-navlink" to={`/product-library/${urlSafeProductId}`}>
            <span>{productName}</span>
          </NavLink>
        )}
      </ProductCardTextContainer>
    </ProductCardStyles>
  );
}
